/**
 * Expression System
 *
 * Exports expression evaluation functionality.
 * Users write standard JavaScript with workflow context functions.
 *
 * SECURITY: Runtime expression evaluation MUST use the async worker-based
 * evaluator (evaluateAsync / evaluateBatchAsync). The sync evaluateTemplate
 * is retained only for unit tests and MUST NOT be called in production code
 * (it executes user-provided code in the main thread without isolation).
 */

// Types
export type {
  EvaluationContext,
  EvaluationResult,
  AutocompleteSuggestion,
  ExtensionMethodMeta,
  TypeExtensions,
} from './types';

// Sync evaluator helpers — only the NON-evaluating utilities are re-exported here.
// The sync `evaluateExpression` / `evaluateTemplate` execute user code via `new Function()`
// on the main thread and are intentionally NOT re-exported (production must use the async
// worker evaluator below). Tests that need the sync evaluator import it directly from
// '@/utils/expressions/evaluator'.
export { canEvaluate, LOOP_FUNCTION_PATTERN } from './evaluator';

// Async worker-based evaluator — use this in all runtime/component code.
export { evaluateAsync, evaluateBatchAsync, terminateWorker } from './workerEvaluator';

// Template wrapping
export {
  wrapAsExpression,
  containsTemplateToken,
  optionalBracketAccessor,
  pathToOptionalAccessors,
} from './template';

import type { AutocompleteSuggestion, TypeExtensions, ExtensionMethodMeta } from './types';

/**
 * Native JavaScript String methods for autocomplete
 */
export const stringExtensionsMeta: TypeExtensions = {
  typeName: 'String',
  methods: [
    { name: 'toLowerCase', description: 'Returns the string converted to lowercase', returnType: 'string', category: 'Case' },
    { name: 'toUpperCase', description: 'Returns the string converted to uppercase', returnType: 'string', category: 'Case' },
    { name: 'trim', description: 'Removes whitespace from both ends of a string', returnType: 'string', category: 'Whitespace' },
    { name: 'trimStart', description: 'Removes whitespace from the beginning', returnType: 'string', category: 'Whitespace' },
    { name: 'trimEnd', description: 'Removes whitespace from the end', returnType: 'string', category: 'Whitespace' },
    { name: 'split', description: 'Splits a string into an array of substrings', returnType: 'array', args: [{ name: 'separator', type: 'string' }], category: 'Transform' },
    { name: 'slice', description: 'Extracts a section of a string', returnType: 'string', args: [{ name: 'start', type: 'number' }, { name: 'end', type: 'number', optional: true }], category: 'Extract' },
    { name: 'substring', description: 'Returns part of the string between two indices', returnType: 'string', args: [{ name: 'start', type: 'number' }, { name: 'end', type: 'number', optional: true }], category: 'Extract' },
    { name: 'charAt', description: 'Returns the character at the specified index', returnType: 'string', args: [{ name: 'index', type: 'number' }], category: 'Extract' },
    { name: 'replace', description: 'Replaces text in a string', returnType: 'string', args: [{ name: 'searchValue', type: 'string' }, { name: 'replaceValue', type: 'string' }], category: 'Replace' },
    { name: 'replaceAll', description: 'Replaces all occurrences of text', returnType: 'string', args: [{ name: 'searchValue', type: 'string' }, { name: 'replaceValue', type: 'string' }], category: 'Replace' },
    { name: 'includes', description: 'Checks if string contains a substring', returnType: 'boolean', args: [{ name: 'searchString', type: 'string' }], category: 'Search' },
    { name: 'startsWith', description: 'Checks if string starts with specified text', returnType: 'boolean', args: [{ name: 'searchString', type: 'string' }], category: 'Search' },
    { name: 'endsWith', description: 'Checks if string ends with specified text', returnType: 'boolean', args: [{ name: 'searchString', type: 'string' }], category: 'Search' },
    { name: 'indexOf', description: 'Returns the index of first occurrence', returnType: 'number', args: [{ name: 'searchString', type: 'string' }], category: 'Search' },
    { name: 'lastIndexOf', description: 'Returns the index of last occurrence', returnType: 'number', args: [{ name: 'searchString', type: 'string' }], category: 'Search' },
    { name: 'match', description: 'Matches string against a regular expression', returnType: 'array', args: [{ name: 'regexp', type: 'RegExp' }], category: 'Search' },
    { name: 'padStart', description: 'Pads the start of a string', returnType: 'string', args: [{ name: 'targetLength', type: 'number' }, { name: 'padString', type: 'string', optional: true }], category: 'Padding' },
    { name: 'padEnd', description: 'Pads the end of a string', returnType: 'string', args: [{ name: 'targetLength', type: 'number' }, { name: 'padString', type: 'string', optional: true }], category: 'Padding' },
    { name: 'repeat', description: 'Repeats the string a number of times', returnType: 'string', args: [{ name: 'count', type: 'number' }], category: 'Transform' },
    { name: 'concat', description: 'Concatenates strings together', returnType: 'string', args: [{ name: 'strings', type: 'string', variadic: true }], category: 'Transform' },
  ],
  properties: [
    { name: 'length', description: 'Returns the length of the string', returnType: 'number', isProperty: true },
  ],
};

/**
 * Native JavaScript Array methods for autocomplete
 */
export const arrayExtensionsMeta: TypeExtensions = {
  typeName: 'Array',
  methods: [
    { name: 'map', description: 'Creates a new array with the results of calling a function on every element', returnType: 'array', args: [{ name: 'callback', type: 'function' }], category: 'Transform' },
    { name: 'filter', description: 'Creates a new array with elements that pass the test', returnType: 'array', args: [{ name: 'callback', type: 'function' }], category: 'Filter' },
    { name: 'find', description: 'Returns the first element that satisfies the test', returnType: 'any', args: [{ name: 'callback', type: 'function' }], category: 'Search' },
    { name: 'findIndex', description: 'Returns the index of the first element that satisfies the test', returnType: 'number', args: [{ name: 'callback', type: 'function' }], category: 'Search' },
    { name: 'includes', description: 'Checks if array includes an element', returnType: 'boolean', args: [{ name: 'searchElement', type: 'any' }], category: 'Search' },
    { name: 'indexOf', description: 'Returns the index of an element', returnType: 'number', args: [{ name: 'searchElement', type: 'any' }], category: 'Search' },
    { name: 'join', description: 'Joins all elements into a string', returnType: 'string', args: [{ name: 'separator', type: 'string', optional: true }], category: 'Convert' },
    { name: 'slice', description: 'Returns a portion of the array', returnType: 'array', args: [{ name: 'start', type: 'number', optional: true }, { name: 'end', type: 'number', optional: true }], category: 'Extract' },
    { name: 'concat', description: 'Merges two or more arrays', returnType: 'array', args: [{ name: 'arrays', type: 'array', variadic: true }], category: 'Combine' },
    { name: 'reverse', description: 'Reverses the array in place', returnType: 'array', category: 'Transform' },
    { name: 'sort', description: 'Sorts the elements in place', returnType: 'array', args: [{ name: 'compareFunction', type: 'function', optional: true }], category: 'Transform' },
    { name: 'reduce', description: 'Reduces array to a single value', returnType: 'any', args: [{ name: 'callback', type: 'function' }, { name: 'initialValue', type: 'any', optional: true }], category: 'Aggregate' },
    { name: 'some', description: 'Tests whether at least one element passes the test', returnType: 'boolean', args: [{ name: 'callback', type: 'function' }], category: 'Test' },
    { name: 'every', description: 'Tests whether all elements pass the test', returnType: 'boolean', args: [{ name: 'callback', type: 'function' }], category: 'Test' },
    { name: 'flat', description: 'Flattens nested arrays', returnType: 'array', args: [{ name: 'depth', type: 'number', optional: true }], category: 'Transform' },
    { name: 'flatMap', description: 'Maps then flattens the result', returnType: 'array', args: [{ name: 'callback', type: 'function' }], category: 'Transform' },
    { name: 'at', description: 'Returns the element at the given index (supports negative)', returnType: 'any', args: [{ name: 'index', type: 'number' }], category: 'Access' },
  ],
  properties: [
    { name: 'length', description: 'Returns the number of elements', returnType: 'number', isProperty: true },
  ],
};

/**
 * Native JavaScript Number methods for autocomplete
 */
export const numberExtensionsMeta: TypeExtensions = {
  typeName: 'Number',
  methods: [
    { name: 'toFixed', description: 'Formats number with fixed decimal places', returnType: 'string', args: [{ name: 'digits', type: 'number', optional: true }], category: 'Format' },
    { name: 'toString', description: 'Converts number to string', returnType: 'string', args: [{ name: 'radix', type: 'number', optional: true }], category: 'Convert' },
    { name: 'toLocaleString', description: 'Returns a localized string representation', returnType: 'string', args: [{ name: 'locales', type: 'string', optional: true }], category: 'Format' },
    { name: 'toPrecision', description: 'Formats number to specified precision', returnType: 'string', args: [{ name: 'precision', type: 'number', optional: true }], category: 'Format' },
    { name: 'toExponential', description: 'Returns exponential notation', returnType: 'string', args: [{ name: 'fractionDigits', type: 'number', optional: true }], category: 'Format' },
    { name: 'valueOf', description: 'Returns the primitive value of the number', returnType: 'number', category: 'Convert' },
  ],
};

/**
 * Native JavaScript Object methods for autocomplete (static methods)
 */
export const objectExtensionsMeta: TypeExtensions = {
  typeName: 'Object',
  methods: [
    { name: 'Object.keys', description: 'Returns an array of object keys', returnType: 'array', category: 'Access' },
    { name: 'Object.values', description: 'Returns an array of object values', returnType: 'array', category: 'Access' },
    { name: 'Object.entries', description: 'Returns an array of [key, value] pairs', returnType: 'array', category: 'Access' },
    { name: 'Object.assign', description: 'Copies properties from source objects to target', returnType: 'object', args: [{ name: 'target', type: 'object' }, { name: 'source', type: 'object', variadic: true }], category: 'Merge' },
    { name: 'Object.freeze', description: 'Prevents modification of object properties', returnType: 'object', category: 'Immutable' },
    { name: 'Object.hasOwn', description: 'Returns true if object has the specified own property', returnType: 'boolean', args: [{ name: 'obj', type: 'object' }, { name: 'prop', type: 'string' }], category: 'Access' },
    { name: 'Object.fromEntries', description: 'Creates an object from key-value pairs', returnType: 'object', args: [{ name: 'entries', type: 'array' }], category: 'Create' },
  ],
};

/**
 * Native JavaScript Date methods for autocomplete
 */
export const dateExtensionsMeta: TypeExtensions = {
  typeName: 'DateTime',
  methods: [
    { name: 'toISOString', description: 'Returns the date in ISO format', returnType: 'string', category: 'Format' },
    { name: 'toLocaleDateString', description: 'Returns the date portion as a string', returnType: 'string', args: [{ name: 'locales', type: 'string', optional: true }], category: 'Format' },
    { name: 'toLocaleTimeString', description: 'Returns the time portion as a string', returnType: 'string', args: [{ name: 'locales', type: 'string', optional: true }], category: 'Format' },
    { name: 'toLocaleString', description: 'Returns date and time as a string', returnType: 'string', args: [{ name: 'locales', type: 'string', optional: true }], category: 'Format' },
    { name: 'getFullYear', description: 'Returns the year', returnType: 'number', category: 'Get' },
    { name: 'getMonth', description: 'Returns the month (0-11)', returnType: 'number', category: 'Get' },
    { name: 'getDate', description: 'Returns the day of the month', returnType: 'number', category: 'Get' },
    { name: 'getDay', description: 'Returns the day of the week (0-6)', returnType: 'number', category: 'Get' },
    { name: 'getHours', description: 'Returns the hours', returnType: 'number', category: 'Get' },
    { name: 'getMinutes', description: 'Returns the minutes', returnType: 'number', category: 'Get' },
    { name: 'getSeconds', description: 'Returns the seconds', returnType: 'number', category: 'Get' },
    { name: 'getMilliseconds', description: 'Returns the milliseconds', returnType: 'number', category: 'Get' },
    { name: 'getTime', description: 'Returns milliseconds since epoch', returnType: 'number', category: 'Get' },
    { name: 'valueOf', description: 'Returns the primitive value (milliseconds)', returnType: 'number', category: 'Convert' },
  ],
};

/**
 * Helper to convert method metadata to autocomplete suggestion
 */
export function methodToSuggestion(method: ExtensionMethodMeta, category: string): AutocompleteSuggestion {
  const hasArgs = method.args && method.args.length > 0;
  const suggestion: AutocompleteSuggestion = {
    label: method.name,
    type: method.isProperty ? 'property' : 'method',
    detail: `→ ${method.returnType}`,
    documentation: method.description,
    insertText: method.isProperty ? method.name : `${method.name}(${hasArgs ? '' : ''})`,
    category,
    returnType: method.returnType,
  };
  if (method.args) {
    suggestion.args = method.args;
  }
  return suggestion;
}

/**
 * Global helper functions available in expressions
 */
export const globalHelpers: AutocompleteSuggestion[] = [
  // Luxon
  {
    label: 'DateTime',
    type: 'function',
    detail: 'Luxon DateTime',
    documentation: 'Create and manipulate dates/times. Use DateTime.now(), DateTime.fromISO(), etc.',
    insertText: 'DateTime',
    returnType: 'DateTime',
  },
  {
    label: 'DateTime.now()',
    type: 'function',
    detail: 'Current date/time',
    documentation: 'Returns a DateTime representing the current moment',
    insertText: 'DateTime.now()',
    returnType: 'DateTime',
  },
  {
    label: 'DateTime.fromISO()',
    type: 'function',
    detail: 'Parse ISO string',
    documentation: 'Create a DateTime from an ISO 8601 string',
    insertText: "DateTime.fromISO('')",
    returnType: 'DateTime',
  },
  {
    label: 'DateTime.fromJSDate()',
    type: 'function',
    detail: 'From JS Date',
    documentation: 'Create a DateTime from a JavaScript Date object',
    insertText: 'DateTime.fromJSDate(date)',
    returnType: 'DateTime',
  },
  {
    label: 'Duration',
    type: 'function',
    detail: 'Luxon Duration',
    documentation: 'Create and manipulate durations. Use Duration.fromObject(), etc.',
    insertText: 'Duration',
    returnType: 'Duration',
  },
  {
    label: 'Duration.fromObject()',
    type: 'function',
    detail: 'Create duration',
    documentation: 'Create a Duration from an object like { hours: 2, minutes: 30 }',
    insertText: 'Duration.fromObject({ hours: 1 })',
    returnType: 'Duration',
  },
  {
    label: 'Interval',
    type: 'function',
    detail: 'Luxon Interval',
    documentation: 'Create and manipulate time intervals between two DateTimes',
    insertText: 'Interval',
    returnType: 'Interval',
  },
  // JMESPath
  {
    label: 'jmespath()',
    type: 'function',
    detail: 'Query JSON',
    documentation: 'Query JSON data using JMESPath expression. Example: jmespath(data, "people[*].name")',
    insertText: "jmespath(data, 'expression')",
    returnType: 'any',
  },
  // Utilities
  {
    label: 'uuid()',
    type: 'function',
    detail: 'Generate UUID',
    documentation: 'Generates a random UUID v4 string',
    insertText: 'uuid()',
    returnType: 'string',
  },
  {
    label: 'console.log()',
    type: 'function',
    detail: 'Debug output',
    documentation: 'Log a message to the console for debugging',
    insertText: 'console.log()',
    returnType: 'void',
  },
];

/**
 * Workflow context functions available in expressions
 */
export const workflowHelpers: AutocompleteSuggestion[] = [
  {
    label: 'trigger',
    type: 'function',
    detail: 'Trigger data',
    documentation: 'Returns the complete trigger data object',
    insertText: 'trigger()',
    returnType: 'object',
  },
  {
    label: 'triggerBody',
    type: 'function',
    detail: 'Trigger body',
    documentation: 'Returns the body from the trigger',
    insertText: 'triggerBody()',
    returnType: 'any',
  },
  {
    label: 'triggerOutputs',
    type: 'function',
    detail: 'Trigger outputs',
    documentation: 'Returns all outputs from the trigger',
    insertText: 'triggerOutputs()',
    returnType: 'object',
  },
  {
    label: 'body',
    type: 'function',
    detail: 'Action body',
    documentation: 'Returns the body output from a specific action',
    insertText: "body('ActionName')",
    returnType: 'any',
  },
  {
    label: 'outputs',
    type: 'function',
    detail: 'Action outputs',
    documentation: 'Returns all outputs from a specific action',
    insertText: "outputs('ActionName')",
    returnType: 'object',
  },
  {
    label: 'actions',
    type: 'function',
    detail: 'Action info',
    documentation: 'Returns action information including outputs and status',
    insertText: "actions('ActionName')",
    returnType: 'object',
  },
  {
    label: 'variables',
    type: 'function',
    detail: 'Get variable',
    documentation: 'Returns the value of a workflow variable',
    insertText: "variables('VariableName')",
    returnType: 'any',
  },
  {
    label: 'parameters',
    type: 'function',
    detail: 'Get parameter',
    documentation: 'Returns the value of a workflow parameter',
    insertText: "parameters('ParameterName')",
    returnType: 'any',
  },
  {
    label: 'result',
    type: 'function',
    detail: 'Scope result',
    documentation: 'Returns the results (outputs and status) from all actions inside a scoped action (For_each, Until, Scope)',
    insertText: "result('ScopeName')",
    returnType: 'object',
  },
  {
    label: 'item',
    type: 'function',
    detail: 'Current loop item',
    documentation: 'Returns the current item from the innermost For Each loop iteration',
    insertText: 'item()',
    returnType: 'any',
  },
  {
    label: 'items',
    type: 'function',
    detail: 'Loop items',
    documentation: 'Returns the current item from a specific named For Each loop',
    insertText: "items('ForEachLoopName')",
    returnType: 'any',
  },
  {
    label: 'iterationIndexes',
    type: 'function',
    detail: 'Loop index',
    documentation: 'Returns the current iteration index from a specific named loop (For Each or Until)',
    insertText: "iterationIndexes('LoopName')",
    returnType: 'number',
  },
  {
    label: 'workflow',
    type: 'function',
    detail: 'Workflow metadata',
    documentation: 'Returns workflow metadata including name, id, run id, and location',
    insertText: 'workflow()',
    returnType: 'object',
  },
];

/**
 * Common JavaScript suggestions for autocomplete
 */
export const javaScriptHelpers: AutocompleteSuggestion[] = [
  // Common patterns
  {
    label: 'new Date()',
    type: 'function',
    detail: 'Current date/time',
    documentation: 'Creates a new Date object with the current date and time',
    insertText: 'new Date()',
    returnType: 'Date',
  },
  {
    label: 'JSON.stringify',
    type: 'function',
    detail: 'Convert to JSON string',
    documentation: 'Converts a JavaScript value to a JSON string',
    insertText: 'JSON.stringify(value)',
    returnType: 'string',
  },
  {
    label: 'JSON.parse',
    type: 'function',
    detail: 'Parse JSON string',
    documentation: 'Parses a JSON string and returns a JavaScript value',
    insertText: 'JSON.parse(text)',
    returnType: 'any',
  },
  {
    label: 'Math.round',
    type: 'function',
    detail: 'Round number',
    documentation: 'Returns the value of a number rounded to the nearest integer',
    insertText: 'Math.round(value)',
    returnType: 'number',
  },
  {
    label: 'Math.floor',
    type: 'function',
    detail: 'Round down',
    documentation: 'Returns the largest integer less than or equal to a number',
    insertText: 'Math.floor(value)',
    returnType: 'number',
  },
  {
    label: 'Math.ceil',
    type: 'function',
    detail: 'Round up',
    documentation: 'Returns the smallest integer greater than or equal to a number',
    insertText: 'Math.ceil(value)',
    returnType: 'number',
  },
  {
    label: 'Math.max',
    type: 'function',
    detail: 'Maximum value',
    documentation: 'Returns the largest of zero or more numbers',
    insertText: 'Math.max(a, b)',
    returnType: 'number',
  },
  {
    label: 'Math.min',
    type: 'function',
    detail: 'Minimum value',
    documentation: 'Returns the smallest of zero or more numbers',
    insertText: 'Math.min(a, b)',
    returnType: 'number',
  },
  {
    label: 'encodeURIComponent',
    type: 'function',
    detail: 'URL encode',
    documentation: 'Encodes a text string as a valid URI component',
    insertText: 'encodeURIComponent(text)',
    returnType: 'string',
  },
  {
    label: 'decodeURIComponent',
    type: 'function',
    detail: 'URL decode',
    documentation: 'Decodes a URI component',
    insertText: 'decodeURIComponent(text)',
    returnType: 'string',
  },
  {
    label: 'btoa',
    type: 'function',
    detail: 'Base64 encode',
    documentation: 'Creates a base64 encoded ASCII string from a string',
    insertText: 'btoa(text)',
    returnType: 'string',
  },
  {
    label: 'atob',
    type: 'function',
    detail: 'Base64 decode',
    documentation: 'Decodes a base64 encoded string',
    insertText: 'atob(text)',
    returnType: 'string',
  },
  {
    label: 'parseInt',
    type: 'function',
    detail: 'Parse integer',
    documentation: 'Parses a string and returns an integer',
    insertText: 'parseInt(text, 10)',
    returnType: 'number',
  },
  {
    label: 'parseFloat',
    type: 'function',
    detail: 'Parse float',
    documentation: 'Parses a string and returns a floating point number',
    insertText: 'parseFloat(text)',
    returnType: 'number',
  },
  // URL API
  {
    label: 'new URL()',
    type: 'function',
    detail: 'Parse URL',
    documentation: 'Parse a URL string. Access .hostname, .pathname, .search, .port, .protocol, .hash properties',
    insertText: "new URL('https://example.com')",
    returnType: 'URL',
  },
  // Type checking
  {
    label: 'Number.isInteger',
    type: 'function',
    detail: 'Check integer',
    documentation: 'Returns true if the value is an integer (no decimal part)',
    insertText: 'Number.isInteger(value)',
    returnType: 'boolean',
  },
  {
    label: 'Number.isFinite',
    type: 'function',
    detail: 'Check finite',
    documentation: 'Returns true if the value is a finite number (not Infinity or NaN)',
    insertText: 'Number.isFinite(value)',
    returnType: 'boolean',
  },
  {
    label: 'Number.isNaN',
    type: 'function',
    detail: 'Check NaN',
    documentation: 'Returns true if the value is NaN (Not a Number)',
    insertText: 'Number.isNaN(value)',
    returnType: 'boolean',
  },
  {
    label: 'typeof',
    type: 'function',
    detail: 'Type check operator',
    documentation: 'Returns the type of a value as a string: "string", "number", "boolean", "object", "undefined", "function"',
    insertText: 'typeof value',
    returnType: 'string',
  },
  // Array utilities
  {
    label: 'Array.from',
    type: 'function',
    detail: 'Create array',
    documentation: 'Creates a new array from an iterable or array-like object. Useful for converting Sets, Maps, or generating sequences',
    insertText: 'Array.from(iterable)',
    returnType: 'array',
  },
  {
    label: 'Array.isArray',
    type: 'function',
    detail: 'Check array',
    documentation: 'Returns true if the value is an array',
    insertText: 'Array.isArray(value)',
    returnType: 'boolean',
  },
  // Object utilities
  {
    label: 'Object.assign',
    type: 'function',
    detail: 'Merge objects',
    documentation: 'Copies all properties from source objects to target object. Use to merge objects: Object.assign({}, obj1, obj2)',
    insertText: 'Object.assign({}, source1, source2)',
    returnType: 'object',
  },
  {
    label: 'Object.freeze',
    type: 'function',
    detail: 'Freeze object',
    documentation: 'Prevents modification of existing object properties and values',
    insertText: 'Object.freeze(obj)',
    returnType: 'object',
  },
  {
    label: 'Object.hasOwn',
    type: 'function',
    detail: 'Has property',
    documentation: 'Returns true if the object has the specified property as its own (not inherited)',
    insertText: "Object.hasOwn(obj, 'property')",
    returnType: 'boolean',
  },
  // Additional Math
  {
    label: 'Math.abs',
    type: 'function',
    detail: 'Absolute value',
    documentation: 'Returns the absolute value of a number',
    insertText: 'Math.abs(value)',
    returnType: 'number',
  },
  {
    label: 'Math.pow',
    type: 'function',
    detail: 'Power',
    documentation: 'Returns base raised to the exponent power. Equivalent to base ** exponent',
    insertText: 'Math.pow(base, exponent)',
    returnType: 'number',
  },
  {
    label: 'Math.random',
    type: 'function',
    detail: 'Random 0-1',
    documentation: 'Returns a random floating-point number between 0 (inclusive) and 1 (exclusive)',
    insertText: 'Math.random()',
    returnType: 'number',
  },
  {
    label: 'Math.sqrt',
    type: 'function',
    detail: 'Square root',
    documentation: 'Returns the square root of a number',
    insertText: 'Math.sqrt(value)',
    returnType: 'number',
  },
  {
    label: 'Math.trunc',
    type: 'function',
    detail: 'Truncate',
    documentation: 'Returns the integer part of a number by removing decimal digits',
    insertText: 'Math.trunc(value)',
    returnType: 'number',
  },
  // Encoding
  {
    label: 'TextEncoder',
    type: 'function',
    detail: 'UTF-8 encode',
    documentation: 'Encode a string to UTF-8 bytes. Use new TextEncoder().encode(text) to get a Uint8Array',
    insertText: 'new TextEncoder().encode(text)',
    returnType: 'Uint8Array',
  },
  {
    label: 'TextDecoder',
    type: 'function',
    detail: 'UTF-8 decode',
    documentation: 'Decode UTF-8 bytes to a string. Use new TextDecoder().decode(bytes)',
    insertText: 'new TextDecoder().decode(bytes)',
    returnType: 'string',
  },
  // String utilities
  {
    label: 'String.fromCharCode',
    type: 'function',
    detail: 'Char from code',
    documentation: 'Returns a string from the specified Unicode values',
    insertText: 'String.fromCharCode(code)',
    returnType: 'string',
  },
  {
    label: 'RegExp',
    type: 'function',
    detail: 'Regular expression',
    documentation: 'Create a regular expression for pattern matching. Use /pattern/flags or new RegExp(pattern, flags)',
    insertText: '/pattern/g',
    returnType: 'RegExp',
  },
];

/**
 * Luxon DateTime method suggestions for expression autocomplete
 * These surface when user types DateTime. or has a DateTime value
 */
export const luxonHelpers: AutocompleteSuggestion[] = [
  // Creation methods
  {
    label: 'DateTime.fromFormat()',
    type: 'function',
    detail: 'Parse formatted string',
    documentation: "Parse a date string with a specific format. Example: DateTime.fromFormat('05/25/2023', 'MM/dd/yyyy')",
    insertText: "DateTime.fromFormat('dateString', 'format')",
    returnType: 'DateTime',
  },
  {
    label: 'DateTime.fromMillis()',
    type: 'function',
    detail: 'From epoch ms',
    documentation: 'Create a DateTime from milliseconds since epoch (Unix timestamp * 1000)',
    insertText: 'DateTime.fromMillis(milliseconds)',
    returnType: 'DateTime',
  },
  {
    label: 'DateTime.fromSeconds()',
    type: 'function',
    detail: 'From epoch seconds',
    documentation: 'Create a DateTime from seconds since epoch (Unix timestamp)',
    insertText: 'DateTime.fromSeconds(seconds)',
    returnType: 'DateTime',
  },
  {
    label: 'DateTime.utc()',
    type: 'function',
    detail: 'UTC DateTime',
    documentation: 'Create a DateTime in UTC from year, month, day, hour, etc. components',
    insertText: 'DateTime.utc(year, month, day)',
    returnType: 'DateTime',
  },
  // Arithmetic (covers getFutureTime, getPastTime gaps)
  {
    label: '.plus()',
    type: 'method',
    detail: 'Add duration',
    documentation: "Add time to a DateTime. Example: DateTime.now().plus({ hours: 2, minutes: 30 }). Supports: years, months, weeks, days, hours, minutes, seconds, milliseconds",
    insertText: '.plus({ hours: 1 })',
    returnType: 'DateTime',
    category: 'DateTime Arithmetic',
  },
  {
    label: '.minus()',
    type: 'method',
    detail: 'Subtract duration',
    documentation: "Subtract time from a DateTime. Example: DateTime.now().minus({ days: 7 }). Supports: years, months, weeks, days, hours, minutes, seconds, milliseconds",
    insertText: '.minus({ days: 1 })',
    returnType: 'DateTime',
    category: 'DateTime Arithmetic',
  },
  // Boundaries (covers startOfHour gap)
  {
    label: '.startOf()',
    type: 'method',
    detail: 'Start of unit',
    documentation: "Set to the start of a time unit. Example: DateTime.now().startOf('hour'), .startOf('day'), .startOf('month'), .startOf('year'), .startOf('week')",
    insertText: ".startOf('hour')",
    returnType: 'DateTime',
    category: 'DateTime Boundaries',
  },
  {
    label: '.endOf()',
    type: 'method',
    detail: 'End of unit',
    documentation: "Set to the end of a time unit. Example: DateTime.now().endOf('day'), .endOf('month'), .endOf('year')",
    insertText: ".endOf('day')",
    returnType: 'DateTime',
    category: 'DateTime Boundaries',
  },
  // Diff (covers dateDifference gap)
  {
    label: '.diff()',
    type: 'method',
    detail: 'Difference',
    documentation: "Get the difference between two DateTimes. Example: dt1.diff(dt2, 'days').days or dt1.diff(dt2, ['hours', 'minutes'])",
    insertText: ".diff(otherDateTime, 'days')",
    returnType: 'Duration',
    category: 'DateTime Comparison',
  },
  {
    label: '.hasSame()',
    type: 'method',
    detail: 'Same unit?',
    documentation: "Check if two DateTimes are in the same unit. Example: dt1.hasSame(dt2, 'day') — true if same calendar day",
    insertText: ".hasSame(otherDateTime, 'day')",
    returnType: 'boolean',
    category: 'DateTime Comparison',
  },
  // Timezone (covers convertTimeZone gap)
  {
    label: '.setZone()',
    type: 'method',
    detail: 'Change timezone',
    documentation: "Convert to a different timezone. Example: DateTime.now().setZone('America/New_York'), .setZone('UTC'), .setZone('Asia/Tokyo')",
    insertText: ".setZone('America/New_York')",
    returnType: 'DateTime',
    category: 'DateTime Timezone',
  },
  {
    label: '.toUTC()',
    type: 'method',
    detail: 'Convert to UTC',
    documentation: 'Convert the DateTime to UTC timezone',
    insertText: '.toUTC()',
    returnType: 'DateTime',
    category: 'DateTime Timezone',
  },
  {
    label: '.toLocal()',
    type: 'method',
    detail: 'Convert to local',
    documentation: 'Convert the DateTime to the local system timezone',
    insertText: '.toLocal()',
    returnType: 'DateTime',
    category: 'DateTime Timezone',
  },
  // Formatting (covers formatDateTime, formatTimeSpan gaps)
  {
    label: '.toFormat()',
    type: 'method',
    detail: 'Custom format',
    documentation: "Format a DateTime with a custom format string. Common tokens: yyyy (year), MM (month), dd (day), HH (24h hour), mm (minute), ss (second). Example: DateTime.now().toFormat('yyyy-MM-dd HH:mm')",
    insertText: ".toFormat('yyyy-MM-dd HH:mm:ss')",
    returnType: 'string',
    category: 'DateTime Format',
  },
  {
    label: '.toISO()',
    type: 'method',
    detail: 'ISO 8601 string',
    documentation: 'Returns the DateTime as an ISO 8601 string. Example: "2023-05-25T14:30:00.000Z"',
    insertText: '.toISO()',
    returnType: 'string',
    category: 'DateTime Format',
  },
  {
    label: '.toRelative()',
    type: 'method',
    detail: 'Relative time',
    documentation: 'Returns a human-readable relative time string. Example: "in 2 hours", "3 days ago"',
    insertText: '.toRelative()',
    returnType: 'string',
    category: 'DateTime Format',
  },
  {
    label: '.toMillis()',
    type: 'method',
    detail: 'Epoch milliseconds',
    documentation: 'Returns the epoch timestamp in milliseconds',
    insertText: '.toMillis()',
    returnType: 'number',
    category: 'DateTime Format',
  },
  {
    label: '.toSeconds()',
    type: 'method',
    detail: 'Epoch seconds',
    documentation: 'Returns the epoch timestamp in seconds',
    insertText: '.toSeconds()',
    returnType: 'number',
    category: 'DateTime Format',
  },
  // Getters (covers dayOfWeek, dayOfMonth, dayOfYear equivalents)
  {
    label: '.year',
    type: 'property',
    detail: 'Year',
    documentation: 'The year (e.g., 2023)',
    insertText: '.year',
    returnType: 'number',
    category: 'DateTime Properties',
  },
  {
    label: '.month',
    type: 'property',
    detail: 'Month (1-12)',
    documentation: 'The month (1-12, January = 1)',
    insertText: '.month',
    returnType: 'number',
    category: 'DateTime Properties',
  },
  {
    label: '.day',
    type: 'property',
    detail: 'Day of month',
    documentation: 'The day of the month (1-31)',
    insertText: '.day',
    returnType: 'number',
    category: 'DateTime Properties',
  },
  {
    label: '.hour',
    type: 'property',
    detail: 'Hour (0-23)',
    documentation: 'The hour of the day (0-23)',
    insertText: '.hour',
    returnType: 'number',
    category: 'DateTime Properties',
  },
  {
    label: '.minute',
    type: 'property',
    detail: 'Minute (0-59)',
    documentation: 'The minute of the hour (0-59)',
    insertText: '.minute',
    returnType: 'number',
    category: 'DateTime Properties',
  },
  {
    label: '.second',
    type: 'property',
    detail: 'Second (0-59)',
    documentation: 'The second of the minute (0-59)',
    insertText: '.second',
    returnType: 'number',
    category: 'DateTime Properties',
  },
  {
    label: '.weekday',
    type: 'property',
    detail: 'Day of week (1-7)',
    documentation: 'The day of the week (1 = Monday, 7 = Sunday, ISO standard)',
    insertText: '.weekday',
    returnType: 'number',
    category: 'DateTime Properties',
  },
  {
    label: '.ordinal',
    type: 'property',
    detail: 'Day of year',
    documentation: 'The ordinal day of the year (1-366)',
    insertText: '.ordinal',
    returnType: 'number',
    category: 'DateTime Properties',
  },
  {
    label: '.zoneName',
    type: 'property',
    detail: 'Timezone name',
    documentation: 'The IANA timezone name (e.g., "America/New_York", "UTC")',
    insertText: '.zoneName',
    returnType: 'string',
    category: 'DateTime Properties',
  },
  // Duration helpers
  {
    label: 'Duration.fromISO()',
    type: 'function',
    detail: 'Parse ISO duration',
    documentation: "Parse an ISO 8601 duration string. Example: Duration.fromISO('PT1H30M') for 1 hour 30 minutes",
    insertText: "Duration.fromISO('PT1H')",
    returnType: 'Duration',
  },
  {
    label: 'Duration.fromMillis()',
    type: 'function',
    detail: 'Duration from ms',
    documentation: 'Create a Duration from a number of milliseconds',
    insertText: 'Duration.fromMillis(milliseconds)',
    returnType: 'Duration',
  },
  {
    label: '.as()',
    type: 'method',
    detail: 'Duration in unit',
    documentation: "Convert a Duration to a specific unit. Example: duration.as('hours'), .as('minutes'), .as('seconds')",
    insertText: ".as('hours')",
    returnType: 'number',
    category: 'Duration',
  },
  {
    label: '.toFormat()',
    type: 'method',
    detail: 'Format duration',
    documentation: "Format a Duration. Example: duration.toFormat('hh:mm:ss'), .toFormat('d days, h hours')",
    insertText: ".toFormat('hh:mm:ss')",
    returnType: 'string',
    category: 'Duration',
  },
  {
    label: '.toISO()',
    type: 'method',
    detail: 'Duration to ISO',
    documentation: "Returns the Duration as an ISO 8601 duration string (e.g., 'PT1H30M')",
    insertText: '.toISO()',
    returnType: 'string',
    category: 'Duration',
  },
];

/**
 * Get all available autocomplete suggestions
 */
export function getAllSuggestions(): AutocompleteSuggestion[] {
  return [...workflowHelpers, ...javaScriptHelpers, ...luxonHelpers];
}

/**
 * Infer the type from a string expression pattern (static analysis)
 */
export function inferTypeFromExpression(expr: string): string {
  const trimmed = expr.trim();

  // String literals
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
    (trimmed.startsWith('`') && trimmed.endsWith('`'))
  ) {
    return 'string';
  }

  // Array literal
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    return 'array';
  }

  // Object literal
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    return 'object';
  }

  // Number literal
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return 'number';
  }

  // Boolean literal
  if (trimmed === 'true' || trimmed === 'false') {
    return 'boolean';
  }

  // Null/undefined
  if (trimmed === 'null' || trimmed === 'undefined') {
    return 'any';
  }

  // Known function return types
  if (
    trimmed.startsWith('trigger(') ||
    trimmed.startsWith('outputs(') ||
    trimmed.startsWith('actions(')
  ) {
    return 'object';
  }
  if (trimmed.startsWith('body(') || trimmed.startsWith('triggerBody()')) {
    return 'any';
  }
  if (trimmed.startsWith('new Date(')) {
    return 'Date';
  }

  return 'any';
}

/**
 * Get methods/properties for a value based on its runtime type
 * Returns common JavaScript methods users might want to use
 */
export function getMethodsForValue(value: unknown): AutocompleteSuggestion[] {
  if (value === null || value === undefined) {
    return [];
  }

  if (typeof value === 'string') {
    return [
      { label: 'length', type: 'property', detail: 'String length', documentation: 'Returns the length of the string', insertText: 'length', returnType: 'number' },
      { label: 'toLowerCase()', type: 'method', detail: 'To lowercase', documentation: 'Returns the string converted to lowercase', insertText: 'toLowerCase()', returnType: 'string' },
      { label: 'toUpperCase()', type: 'method', detail: 'To uppercase', documentation: 'Returns the string converted to uppercase', insertText: 'toUpperCase()', returnType: 'string' },
      { label: 'trim()', type: 'method', detail: 'Trim whitespace', documentation: 'Removes whitespace from both ends', insertText: 'trim()', returnType: 'string' },
      { label: 'split()', type: 'method', detail: 'Split string', documentation: 'Splits a string into an array of substrings', insertText: "split(',')", returnType: 'array' },
      { label: 'slice()', type: 'method', detail: 'Extract section', documentation: 'Extracts a section of a string', insertText: 'slice(0, 10)', returnType: 'string' },
      { label: 'substring()', type: 'method', detail: 'Get substring', documentation: 'Returns part of the string', insertText: 'substring(0, 10)', returnType: 'string' },
      { label: 'replace()', type: 'method', detail: 'Replace text', documentation: 'Replaces text in a string', insertText: "replace('old', 'new')", returnType: 'string' },
      { label: 'includes()', type: 'method', detail: 'Check contains', documentation: 'Checks if string contains a substring', insertText: "includes('text')", returnType: 'boolean' },
      { label: 'startsWith()', type: 'method', detail: 'Check start', documentation: 'Checks if string starts with text', insertText: "startsWith('text')", returnType: 'boolean' },
      { label: 'endsWith()', type: 'method', detail: 'Check end', documentation: 'Checks if string ends with text', insertText: "endsWith('text')", returnType: 'boolean' },
      { label: 'indexOf()', type: 'method', detail: 'Find index', documentation: 'Returns the index of first occurrence', insertText: "indexOf('text')", returnType: 'number' },
      { label: 'charAt()', type: 'method', detail: 'Get character', documentation: 'Returns the character at an index', insertText: 'charAt(0)', returnType: 'string' },
      { label: 'match()', type: 'method', detail: 'Regex match', documentation: 'Matches a string against a regex', insertText: 'match(/pattern/)', returnType: 'array' },
      { label: 'padStart()', type: 'method', detail: 'Pad start', documentation: 'Pads the string at the start', insertText: "padStart(10, '0')", returnType: 'string' },
      { label: 'padEnd()', type: 'method', detail: 'Pad end', documentation: 'Pads the string at the end', insertText: "padEnd(10, '0')", returnType: 'string' },
    ];
  }

  if (Array.isArray(value)) {
    return [
      { label: 'length', type: 'property', detail: 'Array length', documentation: 'Returns the number of elements', insertText: 'length', returnType: 'number' },
      { label: '[0]', type: 'property', detail: 'First element', documentation: 'Access the first element', insertText: '[0]', returnType: 'any' },
      { label: 'map()', type: 'method', detail: 'Map array', documentation: 'Creates a new array with the results of calling a function on every element', insertText: 'map(x => x)', returnType: 'array' },
      { label: 'filter()', type: 'method', detail: 'Filter array', documentation: 'Creates a new array with elements that pass the test', insertText: 'filter(x => x)', returnType: 'array' },
      { label: 'find()', type: 'method', detail: 'Find element', documentation: 'Returns the first element that passes the test', insertText: 'find(x => x)', returnType: 'any' },
      { label: 'includes()', type: 'method', detail: 'Check contains', documentation: 'Checks if array includes an element', insertText: 'includes(value)', returnType: 'boolean' },
      { label: 'indexOf()', type: 'method', detail: 'Find index', documentation: 'Returns the index of an element', insertText: 'indexOf(value)', returnType: 'number' },
      { label: 'join()', type: 'method', detail: 'Join to string', documentation: 'Joins all elements into a string', insertText: "join(', ')", returnType: 'string' },
      { label: 'slice()', type: 'method', detail: 'Extract section', documentation: 'Returns a portion of the array', insertText: 'slice(0, 10)', returnType: 'array' },
      { label: 'concat()', type: 'method', detail: 'Merge arrays', documentation: 'Merges two or more arrays', insertText: 'concat(otherArray)', returnType: 'array' },
      { label: 'reverse()', type: 'method', detail: 'Reverse array', documentation: 'Reverses the array in place', insertText: 'reverse()', returnType: 'array' },
      { label: 'sort()', type: 'method', detail: 'Sort array', documentation: 'Sorts the elements in place', insertText: 'sort()', returnType: 'array' },
      { label: 'reduce()', type: 'method', detail: 'Reduce array', documentation: 'Reduces array to a single value', insertText: 'reduce((acc, x) => acc + x, 0)', returnType: 'any' },
      { label: 'some()', type: 'method', detail: 'Test some', documentation: 'Tests whether at least one element passes', insertText: 'some(x => x)', returnType: 'boolean' },
      { label: 'every()', type: 'method', detail: 'Test every', documentation: 'Tests whether all elements pass', insertText: 'every(x => x)', returnType: 'boolean' },
      { label: 'flat()', type: 'method', detail: 'Flatten array', documentation: 'Flattens nested arrays', insertText: 'flat()', returnType: 'array' },
    ];
  }

  if (typeof value === 'number') {
    return [
      { label: 'toFixed()', type: 'method', detail: 'Fixed decimals', documentation: 'Formats a number with fixed decimals', insertText: 'toFixed(2)', returnType: 'string' },
      { label: 'toString()', type: 'method', detail: 'To string', documentation: 'Returns a string representation', insertText: 'toString()', returnType: 'string' },
      { label: 'toLocaleString()', type: 'method', detail: 'Locale string', documentation: 'Returns a localized string representation', insertText: 'toLocaleString()', returnType: 'string' },
    ];
  }

  if (value instanceof Date) {
    return [
      { label: 'toISOString()', type: 'method', detail: 'ISO format', documentation: 'Returns the date in ISO format', insertText: 'toISOString()', returnType: 'string' },
      { label: 'toLocaleDateString()', type: 'method', detail: 'Local date', documentation: 'Returns the date portion as a string', insertText: 'toLocaleDateString()', returnType: 'string' },
      { label: 'toLocaleTimeString()', type: 'method', detail: 'Local time', documentation: 'Returns the time portion as a string', insertText: 'toLocaleTimeString()', returnType: 'string' },
      { label: 'getFullYear()', type: 'method', detail: 'Get year', documentation: 'Returns the year', insertText: 'getFullYear()', returnType: 'number' },
      { label: 'getMonth()', type: 'method', detail: 'Get month', documentation: 'Returns the month (0-11)', insertText: 'getMonth()', returnType: 'number' },
      { label: 'getDate()', type: 'method', detail: 'Get day', documentation: 'Returns the day of the month', insertText: 'getDate()', returnType: 'number' },
      { label: 'getHours()', type: 'method', detail: 'Get hours', documentation: 'Returns the hours', insertText: 'getHours()', returnType: 'number' },
      { label: 'getMinutes()', type: 'method', detail: 'Get minutes', documentation: 'Returns the minutes', insertText: 'getMinutes()', returnType: 'number' },
      { label: 'getTime()', type: 'method', detail: 'Get timestamp', documentation: 'Returns milliseconds since epoch', insertText: 'getTime()', returnType: 'number' },
    ];
  }

  if (typeof value === 'object') {
    return [
      { label: 'Object.keys()', type: 'function', detail: 'Get keys', documentation: 'Returns an array of keys', insertText: 'Object.keys(obj)', returnType: 'array' },
      { label: 'Object.values()', type: 'function', detail: 'Get values', documentation: 'Returns an array of values', insertText: 'Object.values(obj)', returnType: 'array' },
      { label: 'Object.entries()', type: 'function', detail: 'Get entries', documentation: 'Returns an array of [key, value] pairs', insertText: 'Object.entries(obj)', returnType: 'array' },
    ];
  }

  return [];
}

// =============================================================================
// EXPRESSION RESOLVER — parse @-style/${} expressions in run data
// =============================================================================
//
// Resolves Logic Apps / JS template literal expressions against run-data
// snapshots. Used by monitoring + token-rendering surfaces to display the
// real values that a workflow produced at runtime.

export type { ParsedExpression, ResolvedExpression } from './expressionResolver';
export {
  parseExpression,
  resolveExpressionValue,
  formatResolvedValue,
  parseAllExpressions,
  getReferencedActions,
  referencesTrigger,
} from './expressionResolver';

// =============================================================================
// TOKEN TYPE COMPATIBILITY — drag/drop + connection-line type checks
// =============================================================================
//
// Compares token output types to parameter input types so the designer can
// highlight valid drop targets, display compatibility level (exact /
// coercible / uncertain / incompatible), and pick the right type-icon name
// + UI color for token chips.

export type { TypeCompatibilityResult, CompatibilityLevel } from './tokenTypeChecking';
export {
  checkTokenCompatibility,
  isTypeCompatible,
  checkTypeCompatibility,
  getCompatibleTypes,
  checkArrayItemCompatibility,
  checkRequiredProperties,
  getTypeDisplayName,
  getTypeIconName,
  getCompatibilityColor,
} from './tokenTypeChecking';
